const TIMEZONE_OFFSETS = {
  FNT: -2, // Fernando de Noronha
  BRT: -3, // Brasília
  AMT: -4, // Manaus
  ACT: -5  // Rio Branco
};

const MAPA_FUSO_POR_ESTADO: Record<string, string> = {
  AC: 'ACT',
  AM: 'AMT',
  RR: 'AMT',
  PA: 'AMT'
};

export const formatDateTimeWithTimezone = (dateTime: string, timezone: string = 'BRT'): string => {
  if (!dateTime) return '';
  
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} | ${hours}:${minutes} ${timezone}`;
};

export const formatDateTimeWithBrasiliaConversion = (dateTime: string, timezone: string = 'BRT'): string => {
  if (!dateTime) return '';
  
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  if (timezone === 'BRT') {
    return `${day}/${month}/${year} | ${hours}:${minutes}`;
  }
  
  const brasiliaTime = convertToBrasiliaTime(dateTime, timezone);
  const brasiliaHours = String(brasiliaTime.getHours()).padStart(2, '0');
  const brasiliaMinutes = String(brasiliaTime.getMinutes()).padStart(2, '0');
  
  return `${day}/${month}/${year} | ${hours}:${minutes} ${timezone} ( ${brasiliaHours}:${brasiliaMinutes} BRT)`;
};

export const convertToBrasiliaTime = (dateTime: string, fromTimezone: string): Date => {
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return new Date();
  
  const fromOffset = TIMEZONE_OFFSETS[fromTimezone as keyof typeof TIMEZONE_OFFSETS] || -3;
  const brasiliaOffset = TIMEZONE_OFFSETS.BRT;
  const offsetDifference = fromOffset - brasiliaOffset;
  
  const convertedDate = new Date(date);
  convertedDate.setHours(convertedDate.getHours() - offsetDifference);
  
  return convertedDate;
};

export const getTimezoneName = (timezone: string): string => {
  const names = {
    FNT: 'Fernando de Noronha',
    BRT: 'Brasília',
    AMT: 'Manaus',
    ACT: 'Rio Branco'
  };
  
  return names[timezone as keyof typeof names] || timezone;
};

export const getFusoHorarioPorEstado = (estado: string): string => {
  if (!estado) {
    return 'BRT';
  }
  return MAPA_FUSO_POR_ESTADO[estado] || 'BRT';
};

export const getFusoHorarioPorComarca = (comarca: any): string => {
  if (!comarca) {
    return 'BRT';
  }

  if (typeof comarca === 'object' && comarca.fuso_horario) {
    return comarca.fuso_horario;
  }

  const estado = typeof comarca === 'string'
    ? comarca.split(' - ')[0]
    : comarca.uf || comarca.estado || '';

  return getFusoHorarioPorEstado(estado);
};

export const getFusoHorarioPorIdComarca = async (idComarca: number | null | undefined): Promise<string> => {
  if (!idComarca) {
    return 'BRT';
  }

  try {
    const api = (await import('../services/api')).default;
    const response = await api.get(`comarca/${idComarca}`);
    
    if (response.data && response.data.status === 'success' && response.data.result) {
      const comarca = response.data.result;
      
      if (comarca.fuso_horario) {
        return comarca.fuso_horario;
      }
      
      if (comarca.uf) {
        return getFusoHorarioPorEstado(comarca.uf);
      }
    }
    
    if (response.data && response.data.fuso_horario) {
      return response.data.fuso_horario;
    }
    
    if (response.data && response.data.uf) {
      return getFusoHorarioPorEstado(response.data.uf);
    }
    
    return 'BRT';
  } catch (error) {
    console.error('Erro ao buscar fuso horário da comarca:', error);
    return 'BRT';
  }
};

export const formatarDataHora = (data?: string | null): string => {
    if (!data) {
        return "";
    }

    const dataFormatada = new Date(data);

    if (Number.isNaN(dataFormatada.getTime())) {
        return "";
    }

    const dataTexto = dataFormatada.toLocaleDateString("pt-BR");
    const horaTexto = dataFormatada.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
    });

    return `${dataTexto} às ${horaTexto}`;
};

export function formatarTempoRelativo(data?: string | number | Date | null): string {
    if (!data) {
        return "";
    }

    const dataReferencia = new Date();
    const dataInformada = data instanceof Date ? data : new Date(data);

    if (Number.isNaN(dataInformada.getTime())) {
        return "";
    }

    const MILISSEGUNDO = 1000;
    const SEGUNDO = 60;
    const MINUTO = 60;
    const HORA_EM_MILISSEGUNDOS = MILISSEGUNDO * SEGUNDO * MINUTO;
    const DIA_EM_MILISSEGUNDOS = HORA_EM_MILISSEGUNDOS * 24;
    const SEMANA_EM_MILISSEGUNDOS = DIA_EM_MILISSEGUNDOS * 7;
    const MES_EM_MILISSEGUNDOS = DIA_EM_MILISSEGUNDOS * 30;

    const diferencaEmMilissegundos = dataReferencia.getTime() - dataInformada.getTime();

    if (diferencaEmMilissegundos < HORA_EM_MILISSEGUNDOS) {
        return "Atualizada há menos de 1 hora";
    }

    if (diferencaEmMilissegundos < DIA_EM_MILISSEGUNDOS) {
        const horas = Math.floor(diferencaEmMilissegundos / HORA_EM_MILISSEGUNDOS);
        const textoHoras = horas === 1 ? "1 hora" : `${horas} horas`;
        return `Atualizada há ${textoHoras}`;
    }

    if (diferencaEmMilissegundos < SEMANA_EM_MILISSEGUNDOS) {
        const dias = Math.floor(diferencaEmMilissegundos / DIA_EM_MILISSEGUNDOS);
        const textoDias = dias === 1 ? "1 dia" : `${dias} dias`;
        return `Atualizada há ${textoDias}`;
    }

    if (diferencaEmMilissegundos < MES_EM_MILISSEGUNDOS * 4) {
        const semanas = Math.floor(diferencaEmMilissegundos / SEMANA_EM_MILISSEGUNDOS);
        const textoSemanas = semanas === 1 ? "1 semana" : `${semanas} semanas`;
        return `Atualizada há ${textoSemanas}`;
    }

    if (diferencaEmMilissegundos < MES_EM_MILISSEGUNDOS * 12) {
        const meses = Math.floor(diferencaEmMilissegundos / MES_EM_MILISSEGUNDOS);
        const textoMeses = meses === 1 ? "1 mês" : `${meses} meses`;
        return `Atualizada há ${textoMeses}`;
    }

    return "Atualizada há 1 ano";
}
